'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, vaultSpring } from '../lib/utils';
import { useScrollPosition, useViewport } from '../hooks';

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon?: React.ReactNode;
  badge?: number;
}

export interface DesktopNavProps {
  logo: React.ReactNode;
  items: NavItem[];
  activeItem?: string;
  onItemClick?: (item: NavItem) => void;
  rightContent?: React.ReactNode;
  className?: string;
}

/**
 * Desktop: Glassmorphism top sticky navigation
 */
export const DesktopNav: React.FC<DesktopNavProps> = ({
  logo,
  items,
  activeItem,
  onItemClick,
  rightContent,
  className,
}) => {
  const { scrollPosition, scrollDirection } = useScrollPosition();
  const isScrolled = scrollPosition.y > 50;
  const isHidden = scrollDirection === 'down' && scrollPosition.y > 200;

  return (
    <motion.header
      initial={{ y: 0 }}
      animate={{ y: isHidden ? -100 : 0 }}
      transition={vaultSpring}
      className={cn(
        'fixed top-0 left-0 right-0 z-sticky-bar',
        'transition-all duration-300',
        isScrolled ? 'glass-nav' : 'bg-transparent',
        className
      )}
    >
      <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <div className="flex-shrink-0">
          {logo}
        </div>

        {/* Center Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => onItemClick?.(item)}
              className={cn(
                'relative px-4 py-2 rounded-vault text-sm font-medium transition-colors',
                activeItem === item.id
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary'
              )}
            >
              {item.label}
              {activeItem === item.id && (
                <motion.div
                  layoutId="nav-indicator"
                  className="absolute inset-0 bg-white/5 rounded-vault"
                  transition={vaultSpring}
                />
              )}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold-500 text-surface-0 text-xs font-bold flex items-center justify-center">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Right Content */}
        <div className="flex items-center gap-4">
          {rightContent}
        </div>
      </nav>
    </motion.header>
  );
};

export interface MobileNavProps {
  items: NavItem[];
  activeItem?: string;
  onItemClick?: (item: NavItem) => void;
  className?: string;
}

/**
 * Mobile: Bottom tab bar navigation (Thumb Zone)
 */
export const MobileNav: React.FC<MobileNavProps> = ({
  items,
  activeItem,
  onItemClick,
  className,
}) => {
  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-bottom-nav',
        'bg-surface-1/95 backdrop-blur-glass border-t border-white/10',
        'safe-area-bottom',
        className
      )}
    >
      <div className="flex items-center justify-around h-16 px-2">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onItemClick?.(item)}
            className={cn(
              'relative flex flex-col items-center justify-center w-full h-full',
              'transition-colors touch-active'
            )}
          >
            {/* Icon */}
            <div
              className={cn(
                'relative w-6 h-6 mb-1 transition-colors',
                activeItem === item.id ? 'text-gold-500' : 'text-text-muted'
              )}
            >
              {item.icon}
              {/* Active Indicator */}
              {activeItem === item.id && (
                <motion.div
                  layoutId="mobile-nav-indicator"
                  className="absolute -inset-2 bg-gold-500/20 rounded-full -z-10"
                  transition={vaultSpring}
                />
              )}
              {/* Badge */}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] rounded-full bg-vault-error text-white text-[10px] font-bold flex items-center justify-center px-1">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </div>
            {/* Label */}
            <span
              className={cn(
                'text-[10px] font-medium transition-colors',
                activeItem === item.id ? 'text-gold-500' : 'text-text-muted'
              )}
            >
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
};

/**
 * Responsive Navigation - Uses DesktopNav on desktop, MobileNav on mobile
 */
export interface ResponsiveNavProps extends DesktopNavProps, MobileNavProps {}

export const ResponsiveNav: React.FC<ResponsiveNavProps> = (props) => {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return <DesktopNav {...props} />;
  }

  return <MobileNav {...props} />;
};

/**
 * Sticky Bottom Action Bar (Mobile)
 */
export interface StickyActionBarProps {
  children: React.ReactNode;
  show?: boolean;
  className?: string;
}

export const StickyActionBar: React.FC<StickyActionBarProps> = ({
  children,
  show = true,
  className,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={vaultSpring}
          className={cn(
            'fixed bottom-0 left-0 right-0 z-sticky-bar',
            'bg-surface-1/95 backdrop-blur-glass border-t border-white/10',
            'p-4 pb-safe',
            // Account for mobile nav if present
            'mb-16 md:mb-0',
            className
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Breadcrumb Navigation
 */
export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  onItemClick?: (item: BreadcrumbItem, index: number) => void;
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  onItemClick,
  className,
}) => {
  return (
    <nav className={cn('flex items-center gap-2 text-sm', className)}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="text-text-muted">/</span>
          )}
          {item.href && index < items.length - 1 ? (
            <button
              onClick={() => onItemClick?.(item, index)}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              {item.label}
            </button>
          ) : (
            <span className="text-text-primary font-medium">{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

/**
 * Tabs Navigation
 */
export interface TabItem {
  id: string;
  label: string;
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  activeTab,
  onTabChange,
  className,
}) => {
  return (
    <div className={cn('flex gap-1 p-1 bg-surface-2 rounded-vault', className)}>
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => onTabChange(item.id)}
          className={cn(
            'relative flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            activeTab === item.id
              ? 'text-text-primary'
              : 'text-text-secondary hover:text-text-primary'
          )}
        >
          {activeTab === item.id && (
            <motion.div
              layoutId="tab-indicator"
              className="absolute inset-0 bg-surface-3 rounded-lg"
              transition={vaultSpring}
            />
          )}
          <span className="relative flex items-center justify-center gap-2">
            {item.label}
            {item.count !== undefined && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/10">
                {item.count}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
};
